import { useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

function validateLocal(pw) {
  if (!pw || pw.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) return "Password must contain at least one letter and one digit";
  return null;
}

export default function ChangePasswordDialog({ trigger, className = "" }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setPw(""); setPw2(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    const policy = validateLocal(pw);
    if (policy) { toast.error(policy); return; }
    if (current === pw) { toast.error("New password must differ from current"); return; }
    setSaving(true);
    try {
      await api.post("/me/password", { current_password: current, new_password: pw });
      toast.success("Password updated");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className={className} data-testid="change-password-btn">
            <KeyRound className="w-4 h-4 mr-1" /> Password
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription className="text-xs">Minimum 8 characters, must contain a letter and a digit.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Current password</Label>
            <Input type="password" autoComplete="current-password" required data-testid="cp-current-input" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" autoComplete="new-password" required data-testid="cp-new-input" value={pw} onChange={(e) => setPw(e.target.value)} />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" autoComplete="new-password" required data-testid="cp-confirm-input" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }} data-testid="cp-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-slate-950 hover:bg-slate-800" data-testid="cp-save-btn">
              {saving ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
